import { Route, Routes, Navigate } from "react-router-dom";
import HttpsRedirect from "react-https-redirect";
import React, { useState, useEffect } from "react";
import axiosInstance from "@/api/axios";
import { createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { LanguageContext } from "@/context/LanguageContext";

// --- Auth Pages ---
import Layout from "@/pages/Auth-Page/Components/Layout";
import LoginPage from "@/pages/Auth-Page/Components/Login";
import LogoutPage from "@/pages/Auth-Page/Components/Logout";
import PasswordConfirm from "@/pages/Auth-Page/Components/PasswordConfirm";
import PasswordReset from "@/pages/Auth-Page/Components/PasswordReset";
import SignUpPage from "@/pages/Auth-Page/Components/SignUp";
import ThanksPage from "@/pages/Auth-Page/Components/Thanks";

// --- Auth Guard ---
import RequireAuth from "@/pages/Auth-Page/Components/RequireAuth";

// --- Dashboard Pages (Potentially without Sidebar) ---
import BinDashboard from "@/pages/Bin-Dashboard/BinDashboard";
import MainDashboard from "@/pages/Main-Dashboard/MainDashboard";
import Profile from "@/pages/Profile/Profile";
import WasteDashboard from "@/pages/Waste-Dashboard/WasteDashboard";
import Savings from "@/pages/CO2-Savings/Savings";
import SubclassDisplay from "@/pages/Subclass-Display/SubclassDisplay";
import ChangePassword from "@/pages/Change-Password/ChangePassword";
import GHGDashboard from "@/pages/GHG-Dashboard/GHGDashboard";
import LocationsPage from "@/pages/LocationsPage/LocationsPage";
import OthersPage from "@/pages/Others/OthersPage";
import WeightScale from "@/pages/WeightScale/WeightScale";
import Page from "@/app/dashboard/page"; // Demo dashboard page
import Facility from "@/app/dashboard/facility"; // Demo dashboard page
import FeatureRequestsPage from "@/pages/FeatureRequests/FeatureRequestsPage"

// --- Dashboard Pages (Potentially with Sidebar) ---
import RecycleRate from "@/pages/Recycle/Recycle-Rate"; // Circularity page
import Costsaving from "@/pages/costsavings/Costsaving"; // Cost-saving page
import Account from "@/account/account";
import Acc from "@/account/SettingsPage";
import HomePage from "./pages/home/Home_Page.jsx";

// --- Layout ---
import SidebarLayout from "@/common/Components/SideBar/Layout";


import { Toaster } from "@/components/ui/toaster"

// --- Root Redirector ---
// Handles the initial redirection based on the /demo API check
const RootRedirector = () => {
  // State for demo access check
  const [isDemo, setIsDemo] = useState(null);
  const [userCompany, setUserCompany] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const CSU = "CSU";
  const PHASE5 = "phase5";
  const DEMO = "Demo";

  // Effect to check demo access on mount
  useEffect(() => {
    let isMounted = true;
    const checkDemoAccess = async () => {
      setLoading(true);
      setError(null);
      setIsDemo(null); // Reset state on new check
      try {
        // API call to check demo status
        const response = await axiosInstance.get("demo");
        if (isMounted) {
          // Always allow demo access for authenticated users
          setIsDemo(response.data.can_access_demo);
          setUserRole(response.data.user_role); // Store user role
          localStorage.setItem('userRole', response.data.user_role); // Persist user role
          setUserCompany(response.data.user_company); // Store user company
        }
      } catch (err) {
        console.error("Error checking demo access:", err); // Log error
        if (isMounted) {
          setError("Failed to check access status."); // Set error state
          // Default to non-demo access on error for safety, or handle as needed
          setIsDemo(false);
          localStorage.removeItem('userRole'); // Clear role on error
        }
      } finally {
        if (isMounted) {
          setLoading(false); // Stop loading indicator
        }
      }
    };

    checkDemoAccess();

    // Cleanup function to prevent state updates on unmounted component
    return () => {
      isMounted = false;
    };
  }, []); // Empty dependency array means this runs once on mount

  // --- Render Logic ---

  // Display loading indicator while fetching data
  if (loading) {
    return (
      <div className="loader-wrapper">
        <div className="loader"></div>
      </div>
    );
  }

  // Display login page or error message if there was an error
  if (error) {
    // Redirecting to login might be better if the error implies authentication failure
    // return <Navigate to="/login" replace />;
    return <LoginPage/>; // Or show an error message component
  }

  // Once loading is complete and no error, determine the component to render
  if (isDemo !== null) {
    // If demo access is granted, redirect to operations page
    if (isDemo) {
      return <Navigate to="/operations" replace />;
    }else{
      return <Navigate to="/operations" replace />;
    }
  }

  // Fallback if isDemo is still null after loading (shouldn't normally happen)
  return null;
};



// --- Main App Component ---
function App() {
  // State for language preference
  const [language, setLanguage] = useState(localStorage.getItem("language") || "Dutch");


  // Effect to save language preference to local storage
  useEffect(() => {
    localStorage.setItem("language", language);
  }, [language]);

  return (
    // Enforce HTTPS
    <HttpsRedirect>
      <div className="App">
        {/* Provide language context to the entire app */}
        <LanguageContext.Provider value={{ language, setLanguage }}>
          <Toaster />
          {/* Define application routes */}
          <Routes>
            {/* --- Public/Auth Routes (No Sidebar, No Auth Required) --- */}
            <Route path="/" element={<Layout />}> {/* Basic layout for auth pages */}
              <Route path="login" element={<LoginPage />} />
              <Route path="signup" element={<SignUpPage />} />
              <Route path="thanks" element={<ThanksPage />} />
              <Route path="logout" element={<LogoutPage />} />
              <Route path="password/email" element={<PasswordReset />} />
              <Route path="password/confirm" element={<PasswordConfirm />} />
              {/* Add any other public routes here */}
            </Route>

            {/* --- Protected Routes (Auth Required) --- */}
            <Route element={<RequireAuth />}> {/* Wrapper to enforce authentication */}

              {/* Root Index Route: Handled by RootRedirector */}
              <Route index element={<RootRedirector />} />

              {/* Routes WITHOUT Sidebar */}
              <Route path="main-dashboard" element={<MainDashboard />} />
              <Route path="demo-dashboard" element={<Page />} /> {/* Direct access to demo page */}
              <Route path="profile" element={<Profile />} />
              <Route path="bin-dashboard" element={<BinDashboard />} />
              <Route path="waste-dashboard" element={<WasteDashboard />} />
              <Route path="savings" element={<Savings />} />
              <Route path="subclass" element={<SubclassDisplay />} />
              <Route path="change-password" element={<ChangePassword />} />
              <Route path="ghg-dashboard" element={<GHGDashboard />} />
              <Route path="weight-scale" element={<WeightScale />} />
              {/* Add other routes that should NOT have the sidebar here */}

              {/* Routes WITH Sidebar */}
              <Route element={<SidebarLayout />}> {/* Wrapper layout including the sidebar */}
                <Route path="circularity" element={<RecycleRate />} />
                <Route path="operations" element={<Page />} /> {/* This will get the user role from context */}
                <Route path="facility" element={<Facility/>} />
                <Route path="cost-savings" element={<Costsaving/>} />
                <Route path="locations" element={<LocationsPage/>} />
                <Route path="other" element={<OthersPage/>} />
                <Route path="account/*" element={<Acc/>} />
                <Route path="homepage" element={<HomePage/>} />


                {/* Add other routes that should ALWAYS have the sidebar here */}
                {/* Note: The root path's Page component gets the sidebar conditionally via RootRedirector */}
              </Route>

            </Route> {/* End of Protected Routes */}

            {/* Optional: Add a 404 Not Found route */}
            {/* <Route path="*" element={<NotFoundPage />} /> */}

          </Routes>
        </LanguageContext.Provider>
      </div>
    </HttpsRedirect>
  );
}

export default App;

